import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Loader2, Mic, Calendar, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Head Teacher control: per-school caps for paid AI features (Spoken English minutes). */
const UsageCapsCard = () => {
  const [caps, setCaps] = useState({ spoken_english_minutes_per_day: 10, spoken_english_minutes_per_month: 200 });
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/head-teacher/usage-caps`);
        setCaps(res.data);
        setOriginal(res.data);
      } catch (e) {
        console.error(e);
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/head-teacher/usage-caps`, caps);
      setOriginal(caps);
      toast.success('Usage caps updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const dirty = original && (
    original.spoken_english_minutes_per_day !== caps.spoken_english_minutes_per_day ||
    original.spoken_english_minutes_per_month !== caps.spoken_english_minutes_per_month
  );

  if (loading) return <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin text-teal-500" /></div>;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="usage-caps-card">
      <div className="px-5 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <h3 className="font-semibold flex items-center gap-2"><Mic size={18} /> Spoken English — Daily &amp; Monthly Caps</h3>
        <p className="text-xs text-amber-50">Limits the AI cost per student. Whisper STT is ~₹0.50 per minute — caps protect against runaway bills.</p>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1.5">
            <Calendar size={14} /> Daily limit per student
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={60}
              value={caps.spoken_english_minutes_per_day}
              onChange={e => setCaps({ ...caps, spoken_english_minutes_per_day: parseInt(e.target.value, 10) })}
              className="flex-1"
              data-testid="cap-daily-slider"
            />
            <input
              type="number"
              min={0}
              max={120}
              value={caps.spoken_english_minutes_per_day}
              onChange={e => setCaps({ ...caps, spoken_english_minutes_per_day: parseInt(e.target.value || '0', 10) })}
              className="w-20 border rounded-lg px-2 py-1.5 text-sm text-right"
              data-testid="cap-daily-input"
            />
            <span className="text-sm text-gray-500 w-16">min / day</span>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-1.5">
            <Calendar size={14} /> Monthly limit per student
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={1500}
              step={10}
              value={caps.spoken_english_minutes_per_month}
              onChange={e => setCaps({ ...caps, spoken_english_minutes_per_month: parseInt(e.target.value, 10) })}
              className="flex-1"
              data-testid="cap-monthly-slider"
            />
            <input
              type="number"
              min={0}
              max={3000}
              value={caps.spoken_english_minutes_per_month}
              onChange={e => setCaps({ ...caps, spoken_english_minutes_per_month: parseInt(e.target.value || '0', 10) })}
              className="w-20 border rounded-lg px-2 py-1.5 text-sm text-right"
              data-testid="cap-monthly-input"
            />
            <span className="text-sm text-gray-500 w-16">min / month</span>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
          <strong>Estimated cost per student/month:</strong> ~$
          {(caps.spoken_english_minutes_per_month * 0.006).toFixed(2)}
          {' '}(~₹{(caps.spoken_english_minutes_per_month * 0.006 * 84).toFixed(0)}). Set to <strong>0</strong> to disable Spoken English for all students at this school.
        </div>
        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="save-usage-caps-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1" /> Save caps</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UsageCapsCard;
